import axios from 'axios';
import { useMemo, useSyncExternalStore } from 'react';

const FINANCIAL_DATA_AS_TEXT_STORAGE_KEY = 'financialDataAsText:v1';
const FINANCIAL_STATEMENTS_CACHE_TTL_MS = 5 * 60 * 1000;
const FINANCIAL_STATEMENTS_CACHE_VERSION = 'v3';

const EMPTY_FINANCIAL_DATA = {
    titles: {},
    income_statement: [],
    balance_sheet: [],
    cash_flow_statement: [],
};

const initialSnapshot = {
    loading: true,
    financialData: EMPTY_FINANCIAL_DATA,
    availableYears: [],
    statementSourceUrls: {},
};

function getFinancialStatementsCacheKey(ticker, requestedYear) {
    return `financialStatementsResponse_${FINANCIAL_STATEMENTS_CACHE_VERSION}_${String(ticker || '').toUpperCase()}_${requestedYear || 'latest'}`;
}

function readFinancialStatementsCache(cacheKey) {
    try {
        const rawValue = localStorage.getItem(cacheKey);
        if (!rawValue) {
            return null;
        }

        const parsedValue = JSON.parse(rawValue);
        if (!parsedValue?.timestamp || !parsedValue?.payload) {
            return null;
        }

        if (Date.now() - parsedValue.timestamp > FINANCIAL_STATEMENTS_CACHE_TTL_MS) {
            localStorage.removeItem(cacheKey);
            return null;
        }

        return parsedValue.payload;
    } catch (_error) {
        return null;
    }
}

function writeFinancialStatementsCache(cacheKey, payload) {
    try {
        localStorage.setItem(cacheKey, JSON.stringify({
            timestamp: Date.now(),
            payload,
        }));
    } catch (_error) {
        // Ignore cache write failures and continue using the network response.
    }
}

function writeFinancialDataAsTextCache(payload) {
    try {
        localStorage.setItem(FINANCIAL_DATA_AS_TEXT_STORAGE_KEY, JSON.stringify(payload.original));
    } catch (_error) {
        // Ignore cache write failures; financial statements can still render.
    }
}

function createFinancialStatementsStore(ticker, requestedYear, setSearchParams) {
    let cancelled = false;
    let requestStarted = false;
    let snapshot = ticker ? initialSnapshot : { ...initialSnapshot, loading: false };
    const listeners = new Set();

    const notify = () => {
        listeners.forEach((listener) => listener());
    };

    const setSnapshot = (nextSnapshot) => {
        snapshot = nextSnapshot;
        notify();
    };

    const applyFinancialPayload = (payload) => {
        setSnapshot({
            loading: false,
            financialData: payload.fin_dict,
            availableYears: payload.available_years || [],
            statementSourceUrls: payload.statement_source_urls || {},
        });

        if (payload.selected_year) {
            const normalizedYear = String(payload.selected_year);
            if (requestedYear !== normalizedYear) {
                setSearchParams((currentParams) => {
                    const nextParams = new URLSearchParams(currentParams);
                    nextParams.set('year', normalizedYear);
                    return nextParams;
                }, { replace: true });
            }
        }
    };

    const loadFinancialStatements = () => {
        if (!ticker) {
            return;
        }
        cancelled = false;

        const apiBaseUrl = (process.env.REACT_APP_API_URL || '').replace(/\/+$/, '');
        const endpoint = `${apiBaseUrl}/api/financial-data?ticker=${encodeURIComponent(ticker)}${requestedYear ? `&year=${encodeURIComponent(requestedYear)}` : ''}`;
        const cacheKey = getFinancialStatementsCacheKey(ticker, requestedYear);
        const cachedPayload = readFinancialStatementsCache(cacheKey);

        if (cachedPayload) {
            applyFinancialPayload(cachedPayload);
            writeFinancialDataAsTextCache(cachedPayload);
            return;
        }

        setSnapshot({
            ...snapshot,
            loading: true,
            statementSourceUrls: {},
        });

        axios.get(endpoint)
            .then((response) => {
                if (cancelled) {
                    return;
                }
                applyFinancialPayload(response.data);
                writeFinancialStatementsCache(cacheKey, response.data);
                writeFinancialDataAsTextCache(response.data);
                localStorage.setItem(`financialData_${ticker}_${response.data.selected_year ?? 'latest'}`, JSON.stringify(response.data.fin_dict));
                localStorage.setItem('ticker', ticker);
            })
            .catch((error) => {
                if (cancelled) {
                    return;
                }
                console.error('Error fetching financial data:', error);
                setSnapshot({
                    ...snapshot,
                    loading: false,
                });
            });
    };

    return {
        getSnapshot: () => snapshot,
        subscribe: (listener) => {
            listeners.add(listener);
            if (listeners.size === 1 && !requestStarted) {
                requestStarted = true;
                loadFinancialStatements();
            }
            return () => {
                listeners.delete(listener);
                if (listeners.size === 0) {
                    cancelled = true;
                    requestStarted = false;
                }
            };
        },
    };
}

export function useFinancialStatements(ticker, requestedYear, setSearchParams) {
    const store = useMemo(
        () => createFinancialStatementsStore(ticker, requestedYear, setSearchParams),
        [requestedYear, setSearchParams, ticker]
    );

    const state = useSyncExternalStore(
        store.subscribe,
        store.getSnapshot,
        store.getSnapshot
    );

    return {
        loading: state.loading,
        financialData: state.financialData,
        availableYears: state.availableYears,
        statementSourceUrls: state.statementSourceUrls,
    };
}
